import { Component, Input } from '@angular/core';
import { AuthenticationService } from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { SystemsState } from '../../store';
import { setRealTimeTelemetryOverlayHidden } from '../../store/actions/real-time.actions';
import { RtBaseComponent } from '../rt-base/rt-base.component';

const getLabels = () => ({
  ago: 'ago',
  hide: 'Hide',
  last: 'Last',
  realtimeDisconnectedHint: 'To activate real-time telemetry, the plant must be connected.',
  realtimeFirmwareHint: 'To activate real-time telemetry, a logger firmware update is required.',
  show: 'Show',
  start: 'Start',
  stop: 'Stop',
  stopping: 'Stopping',
  stopIn: 'stop in',
  title: 'RT',
});

@CreateBlockReduxSupport()
@Component({
  selector: 'lib-rt-console',
  templateUrl: './rt-console.component.html',
  styleUrls: ['./rt-console.component.scss'],
})
export class RtConsoleComponent extends RtBaseComponent implements IReduxBlock {
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() override refresh$ = new Subject<void>();
  override _refresh$ = new Observable<boolean>();

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly rtConsoleState$ = combineLatest([
    this.rtSession$,
    this.rtAvailability$,
    this.userCanUseRealTime$,
    this.rtTick$,
  ]).pipe(
    map(([session, rtAvailability, userCanUseRealTime]) => {
      const now = Date.now();
      const timing = this.getRTSessionTiming(session, now);

      const lastDataSecondsAgo = session.active && session.lastTelemetryReceivedAt
        ? Math.max(0, Math.round((now - session.lastTelemetryReceivedAt) / 1000))
        : null;

      const effectiveCanUseRealTime = Boolean(rtAvailability.canUseRealTime && userCanUseRealTime);
      const unavailableReason = userCanUseRealTime
        ? rtAvailability.unavailableReason
        : null;
      const showUnavailableReason = Boolean(!session.active && unavailableReason !== null && !effectiveCanUseRealTime);

      return {
        canUseRealTime: effectiveCanUseRealTime,
        countdownSeconds: timing.countdownSeconds,
        isActive: Boolean(session.active),
        isStopping: timing.isStopping,
        isUnavailable: showUnavailableReason,
        lastDataSecondsAgo,
        overlayVisible: Boolean(userCanUseRealTime && session.active && session.overlayRequestedByUser && !session.overlayHidden),
        showConsole: Boolean((session.active && userCanUseRealTime) || effectiveCanUseRealTime || showUnavailableReason),
        unavailableReason,
      };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    override readonly store: Store<SystemsState>,
    authenticationService: AuthenticationService,
  ) {
    super(store, authenticationService);
  }

  onToggleOverlay(isOverlayVisible: boolean): void {
    const systemId = this.system$.value?.uid;
    if (!systemId) {
      return;
    }

    this.store.dispatch(setRealTimeTelemetryOverlayHidden({ systemId, hidden: isOverlayVisible }));
  }
}
